/**
 * @fileoverview Click & Collect Order Store (Zustand)
 * 
 * Manages Click & Collect order state: orders list, pending count, WebSocket handling.
 * Separate from regular orders to keep concerns separated.
 * 
 * @module stores/useClickCollectStore
 */

import { create } from 'zustand';
import { 
  getClickCollectOrders, 
  getClickCollectOrderById 
} from '@/api/click-collect.api';
import { wsService } from '@/services/websocket';

/**
 * Calculate pending Click & Collect orders count
 * Pending = orders with status NOT_PAID (0) or IN_PROGRESS that have items in PENDING status
 */
const calculatePendingCount = (ordersList) => {
  // Count orders that are not paid and have pending menu items
  return ordersList.filter((order) => {
    // Include NOT_PAID orders
    if (order.status === 0) return true;
    // Include IN_PROGRESS orders with pending items
    if (order.status === 1) {
      return order.order_menus?.some((menu) => menu.status === 0);
    }
    return false;
  }).length;
};

/**
 * Click & Collect Order Store
 *
 * @example
 * const ccOrders = useClickCollectStore((state) => state.orders);
 * const fetchOrders = useClickCollectStore((state) => state.fetchOrders);
 */
export const useClickCollectStore = create((set, get) => ({
  // State
  orders: [],
  pendingCount: 0,
  isLoading: false,
  error: null,

  // Internal state
  _initialized: false,
  _wsCleanup: null,
  _router: null,

  // Setters
  setOrders: (orders) => {
    const pendingCount = calculatePendingCount(orders);
    set({ orders, pendingCount });
  },
  setLoading: (isLoading) => set({ isLoading }),
  setError: (error) => set({ error }),
  setRouter: (router) => set({ _router: router }),

  // Fetch all Click & Collect orders
  fetchOrders: async (router, options = {}) => {
    const routerToUse = router || get()._router;
    if (!routerToUse) {
      console.warn('[C&C] No router available for fetchOrders');
      return [];
    }

    set({ isLoading: true, error: null });

    try {
      const orders = await getClickCollectOrders(routerToUse, options);
      const pendingCount = calculatePendingCount(orders);
      set({ orders, pendingCount, isLoading: false });
      return orders;
    } catch (error) {
      console.error('[C&C] Error fetching orders:', error);
      set({ error: error.message, isLoading: false });
      return [];
    }
  },

  // Fetch a single order by ID and add/update it in the store
  fetchOrderById: async (router, orderId) => {
    const routerToUse = router || get()._router;
    if (!routerToUse) {
      console.warn('[C&C] No router available for fetchOrderById');
      return null;
    }

    try {
      const order = await getClickCollectOrderById(routerToUse, orderId);
      if (order) {
        // Add or update order in the list
        const currentOrders = get().orders;
        const existingIndex = currentOrders.findIndex(o => o.id === orderId);
        
        let updatedOrders;
        if (existingIndex >= 0) {
          // Update existing order
          updatedOrders = [...currentOrders];
          updatedOrders[existingIndex] = order;
        } else {
          // Add new order at the beginning
          updatedOrders = [order, ...currentOrders];
        }
        
        const pendingCount = calculatePendingCount(updatedOrders);
        set({ orders: updatedOrders, pendingCount });
      }
      return order;
    } catch (error) {
      console.error(`[C&C] Error fetching order ${orderId}:`, error);
      return null;
    }
  },

  // Handle new Click & Collect order from WebSocket
  handleNewOrder: async (data, router) => {
    const routerToUse = router || get()._router;

    if (data.orderId) {
      try {
        // Fetch the new order by ID
        await get().fetchOrderById(routerToUse, data.orderId);
      } catch (err) {
        console.error('[C&C] Error handling new order:', err);
        // Fallback to full refetch
        await get().fetchOrders(routerToUse);
      }
    } else {
      // No orderId, do a full refetch
      await get().fetchOrders(routerToUse);
    }
  },

  // Handle order update from WebSocket
  handleOrderUpdate: async (data, router) => {
    const routerToUse = router || get()._router;
    // Click & collect endpoints send `order_id`, the others `orderId`.
    const orderId = data?.orderId ?? data?.order_id;

    if (orderId) {
      try {
        // Fetch the updated order by ID
        await get().fetchOrderById(routerToUse, orderId);
      } catch (err) {
        console.error('[C&C] Error handling order update:', err);
        await get().fetchOrders(routerToUse);
      }
    }
  },

  // Remove order from store (for cancelled/paid orders that should be hidden)
  removeOrder: (orderId) => {
    const currentOrders = get().orders;
    const updatedOrders = currentOrders.filter(o => o.id !== orderId);
    const pendingCount = calculatePendingCount(updatedOrders);
    set({ orders: updatedOrders, pendingCount });
  },

  // Initialize WebSocket connection for Click & Collect
  initializeWebSocket: (router) => {
    const user = JSON.parse(localStorage.getItem("userObj") || "{}");
    const restaurant_id = user?.restaurant_id;

    if (!restaurant_id) {
      console.warn('[C&C] No restaurant ID found for WebSocket');
      return;
    }

    // Clean up existing connection
    const existingCleanup = get()._wsCleanup;
    if (existingCleanup) {
      existingCleanup();
    }

    const handleNewOrder = (data) => {
      // Only handle Click & Collect orders
      if (data.message === "New order click & collect placed") {
        get().handleNewOrder(data, router);
      }
    };

    const handleOrderUpdate = (data) => {
      // Check if the update is for an order we're tracking
      const orderId = data.orderId || data.order_id;
      const isTrackedOrder = get().orders.some(o => o.id === orderId);
      if (isTrackedOrder) {
        get().handleOrderUpdate(data, router);
      }
    };

    // Same room as regular orders (shared, ref-counted connection)
    const room = `admin-restaurant:${restaurant_id}`;
    const release = wsService.join(room, { onResync: () => get().fetchOrders(router) });
    const offs = [
      wsService.on('order::new', handleNewOrder, { room }),
      wsService.on('order::update', handleOrderUpdate, { room }),
    ];

    set({
      _wsCleanup: () => {
        offs.forEach((off) => off());
        release();
      },
      _router: router
    });

  },

  // Initialize store
  initialize: async (router) => {
    if (get()._initialized) return;

    set({ _router: router });

    // Initialize WebSocket listeners
    get().initializeWebSocket(router);

    // Fetch initial orders
    await get().fetchOrders(router);

    set({ _initialized: true });

  },

  // Cleanup
  cleanup: () => {
    const wsCleanup = get()._wsCleanup;
    if (wsCleanup) wsCleanup();
    set({
      _initialized: false,
      _wsCleanup: null,
      _router: null,
      orders: [],
      pendingCount: 0
    });
  },

  // Reset store
  reset: () => {
    get().cleanup();
    set({
      orders: [],
      pendingCount: 0,
      isLoading: false,
      error: null,
      _initialized: false,
      _wsCleanup: null,
      _router: null
    });
  }
}));

/**
 * Hook for using Click & Collect store with common selectors
 */
export const useClickCollect = () => {
  const store = useClickCollectStore();

  return {
    orders: store.orders,
    pendingCount: store.pendingCount,
    isLoading: store.isLoading,
    error: store.error,
    fetchOrders: store.fetchOrders,
    fetchOrderById: store.fetchOrderById,
    removeOrder: store.removeOrder,
    initialize: store.initialize,
    cleanup: store.cleanup,
  };
};
